/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import Footer from '../components/custom/navigation/Footer';
import { OptionsConfig } from '../components/custom/modals/OptionsModal';
import CalculationController from '../modules/calculator/CalculationController';
import { CalculatorResult, ParameterModifier, ParameterName } from '../modules/calculator/calculatorTypes';
import UnitController from '../modules/units/UnitController';
import { UnitPreset } from '../modules/units/unitTypes';
import MethodHeader from '../components/custom/navigation/MethodHeader';
import MobileCalculator from '../components/custom/calculators/MobileCalculator';
import DesktopCalculator from '../components/custom/calculators/DesktopCalculator';
import { Container } from 'react-grid-system';
import MethodGrid from '../components/custom/navigation/MethodGrid';
import Graph from '../components/custom/calculators/Graph';
import ScrollNav from '../components/custom/navigation/ScrollNav';
import MainBanner from '../components/custom/navigation/MainBanner';
import { isMobileOnly } from 'react-device-detect';
import { ModalType } from '../components/custom/modals/Modals';
import PersistenceController from '../modules/persistence/PersistenceController';
import ValidityIndicator from '../components/custom/utils/ValidityIndicator';
import { ValidityState } from '../modules/calculator/calculatorTypes';

interface Method {
  id: number;
  heading: string;
  subHeading: string;
  longDesc: string;
}

interface LandingPageProps {
  onHelpOpen: VoidFunction;
  onOptionsOpen: (title: string, config: OptionsConfig) => void;
  unitPresetIndex: number;
  themeIndex: number;
  activeMethod: number;
  onMethodSelect: (method: number) => void;
  theme: any;
  method: Method;
  setIsScrolled: (value: boolean) => void;
  openModal: (type: ModalType, title?: string, optionsConfig?: OptionsConfig, onAccept?: VoidFunction, onReject?: VoidFunction) => void;
}

const LandingPage = ({
  onHelpOpen,
  onOptionsOpen,
  unitPresetIndex,
  activeMethod,
  onMethodSelect,
  theme,
  method,
  setIsScrolled,
  openModal,
}: LandingPageProps) => {
  const [currentResultsForMethods, setCurrentResultsForMethods] = React.useState<CalculatorResult[]>([]);
  const [currentResult, setCurrentResult] = React.useState<CalculatorResult | undefined>(undefined);
  const [currentUnitPreset, setCurrentUnitPreset] = React.useState<UnitPreset | undefined>(undefined);
  const [graphActive, setGraphActive] = React.useState<boolean>(false);
  const [validityState, setValidityState] = React.useState<ValidityState>({ severity: 'off' });

  React.useEffect(() => {
    const storedActiveMethod = PersistenceController.retrieve('activeMethodIndex');
    if (storedActiveMethod > -1) {
      onMethodSelect(storedActiveMethod);
    }
  }, [onMethodSelect]);

  React.useEffect(() => {
    PersistenceController.store('activeMethodIndex', activeMethod);

    if (activeMethod >= 0) {
      const method = CalculationController.indexToMethod(activeMethod);
      setCurrentResultsForMethods(CalculationController.begin(method));
    }
  }, [activeMethod]);

  React.useEffect(() => {
    setCurrentUnitPreset(UnitController.currentPreset);
  }, [unitPresetIndex, currentUnitPreset]);

  React.useEffect(() => {
    if (activeMethod >= 0) {
      const result = currentResultsForMethods[activeMethod];
      setValidityState(result?.validityState);
      setCurrentResult(result);
    }
  }, [activeMethod, currentResultsForMethods]);

  const resetParameters = (log = true) => {
    console.log('reset');
    setCurrentResult(CalculationController.reset(CalculationController.indexToMethod(activeMethod), log));
  };

  const onParameterChange = (cardIndex: number, parameterName: ParameterName, value: ParameterModifier, phase?: number) => {
    if (activeMethod >= 0) {
      const method = CalculationController.indexToMethod(activeMethod);
      const result = CalculationController.calculate(method, cardIndex, parameterName, value, phase);
      setValidityState(result?.validityState);
      setCurrentResult(result);
    }
  };

  // const onSelectUnit = (parameterName: ParameterName, unitIndex: number) => {
  //   UnitController.chooseManualUnit(parameterName, unitIndex);
  //   setCurrentUnitPreset({ ...UnitController.currentPreset });
  // };

  return (
    <Container className={`container ${isMobileOnly ? 'container--mobile' : 'container--desktop'}`}>
      <MethodHeader activeMethod={activeMethod} />
      <MethodGrid theme={theme} activeMethod={activeMethod} onMethodSelect={onMethodSelect} />
      {isMobileOnly && (
        <>
          <MobileCalculator
            activeMethod={activeMethod}
            onParameterChange={onParameterChange}
            currentResult={currentResult}
            theme={theme}
            method={method}
            setIsScrolled={setIsScrolled}
            openModal={openModal}
            currentUnitPreset={currentUnitPreset}
            onReset={resetParameters}
          />
          {activeMethod === 0 && <ScrollNav setGraphActive={setGraphActive} activeMethod={activeMethod} />}
        </>
      )}
      {!isMobileOnly && (
        <DesktopCalculator
          activeMethod={activeMethod}
          onParameterChange={onParameterChange}
          currentResult={currentResult}
          theme={theme}
          onReset={resetParameters}
          onMethodSelect={onMethodSelect}
          openModal={openModal}
          currentUnitPreset={currentUnitPreset}
        />
      )}
      <Graph
        activeMethod={activeMethod}
        graphActive={graphActive}
        setGraphActive={setGraphActive}
        theme={theme}
        onParameterChange={onParameterChange}
        currentResult={currentResult}
        openModal={openModal}
        currentUnitPreset={currentUnitPreset}
      />
      {activeMethod > -1 && <ValidityIndicator state={validityState} onShowHelp={onHelpOpen} />}
      <div
        className={`banner-wrap ${activeMethod === -1 ? 'banner-wrap--active' : ''} ${isMobileOnly ? 'banner-wrap--mobile' : 'banner-wrap--desktop'}`}
      >
        <MainBanner variant={isMobileOnly ? 'narrow' : 'wide'} />
        <div className="gtm-content gtm-content--landing-page" />
        <Footer />
      </div>
    </Container>
  );
};
export default LandingPage;
